import { Box, BoxProps, ColorOptions, ModBox, SpacerNew, TextInputNewProps, TextNew, TextNewProps } from '@jfs/ui-new'
import { ButtonNew } from '../../../components/AdminButton'
import { useCallback, useEffect, useState } from 'react'
import { NewVerticalStepIndicator } from '../../../components/NewVerticalStepIndicator'
import { TextInputLabelToolTip } from '../../../components/TextInputLabelToolTip'
import { useLazyApiData, useStores } from '../../../data/store'
import { FullScreenLoader } from '../OnboardEntity/comps/FullScreenLoader'
import { EntityOnboardingForm } from '../OnboardEntity/EntityOnboardingForm'
import { EntityOnboardingHeader } from '../OnboardEntity/EntityOnboardingHeader'

const IncidentDetails = ({ route, navigation }) => {
  const { resolveIncidentAsync } = useStores((store) => ({
    resolveIncidentAsync: store.entityOnboarding.resolveIncidentAsync,
  }))

  const [currentStep, setCurrentStep] = useState(1)
  const savedData = JSON.parse(localStorage.getItem('queryParams'))
  const entityDetails = JSON.parse(JSON.stringify(route?.params?.item ?? savedData?.item))

  const [lazyResolveIncident, resolveIncidentHandler] = useLazyApiData(resolveIncidentAsync)
  useEffect(() => {
    if (resolveIncidentHandler?.state === 'fulfilled') {
      navigation.reset({
        index: 0,
        routes: [{ name: 'onboardingIncidents' }],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveIncidentHandler?.state])

  const resolveIncident = useCallback(() => {
    lazyResolveIncident({
      requests: [
        {
          jobKey: entityDetails?.jobKey,
          incidentId: entityDetails?.incidentId,
        },
      ],
    })
  }, [entityDetails?.incidentId, entityDetails?.jobKey, lazyResolveIncident])

  useEffect(() => {
    route?.params && localStorage.setItem('queryParams', JSON.stringify(route?.params))
  }, [route?.params])

  const onStepPressed = useCallback((pressedStep: any) => {
    setCurrentStep(pressedStep)
  }, [])

  const onBackPress = useCallback(() => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'onboardingIncidents' }],
    })
  }, [navigation])

  const isObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)
  const convertToReadable = (str) =>
    str
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert spaces between lowercase and uppercase letters
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Handle consecutive uppercase letters
      .replace(/^([a-z])/, (match) => match.toUpperCase()) // Capitalize the first letter
      .replace(/\b([a-z])/g, (match) => match.toUpperCase())

  const renderKeyValuePair = useCallback((object: any) => {
    return (
      <Box flexDirection={'column'}>
        {Object.keys(object).map((key) => {
          const value = object?.[key]
          const isObjectType = isObject(value)
          return (
            <ModBox flexDirection={isObjectType ? 'column' : 'row'} show={value !== null} paddingBottom={4}>
              <Box marginTop={isObjectType ? 16 : 0} marginBottom={isObjectType ? 8 : 0} width={400}>
                <TextNew {...styles.keyText(isObjectType)}>{convertToReadable(key)}</TextNew>
              </Box>
              {isObject(value) && renderKeyValuePair(value)}
              {!isObject(value) && (
                <Box marginLeft={16}>
                  <TextNew {...styles.keyDescText}>{value + ''}</TextNew>
                </Box>
              )}
            </ModBox>
          )
        })}
      </Box>
    )
  }, [])

  return (
    <Box {...styles.container}>
      <EntityOnboardingHeader
        title={entityDetails?.businessDisplayName}
        applicationId={entityDetails?.applicationId}
        applicationStatus={entityDetails?.applicationStatus}
        businessChannel={entityDetails?.businessChannel}
        entityId={entityDetails?.entityIdentificationNumber}
        requestType={entityDetails?.requestType}
        onboardingChannel={entityDetails?.onboardingChannel}
        showBackButton={true}
        onBackPress={onBackPress}
      />
      <ModBox {...styles.innerContainer} show={ONBOARDING_TERMINAL_UPDATE_TYPE.includes(entityDetails?.requestType)}>
        <Box w={220}>
          <NewVerticalStepIndicator
            currentStep={currentStep}
            submitState={''}
            formValidationStatus={INCIDENT_DETAILS_STEPS.map(() => true)}
            steps={INCIDENT_DETAILS_STEPS}
            totalFilledStep={INCIDENT_DETAILS_STEPS.length}
            onStepPressed={onStepPressed}
            type="READ_ONLY"
          />
        </Box>
        <Box flex={1}>
          <EntityOnboardingForm
            currentStep={currentStep}
            onStepPressed={onStepPressed}
            initialValues={entityDetails}
            isEditable={false}
            fromChekerQueue={true}
            setIsCurrentFormValid={() => {}}
            onSubmit={undefined}
            userType={'INCIDENT_DETAILS'}
          />
        </Box>
      </ModBox>
      <ModBox
        {...styles.innerContainer}
        show={!ONBOARDING_TERMINAL_UPDATE_TYPE.includes(entityDetails?.requestType)}
        overflowY={'scroll'}
        pv={16}
        ph={24}>
        {renderKeyValuePair(entityDetails.state)}
      </ModBox>

      <Box
        flexDirection="row"
        padding={24}
        borderTopColor={'BORDER_COLOR'}
        borderTopWidth={1}
        justifyContent={'space-between'}
        alignItems={'baseline'}>
        <Box f={1}>
          <TextInputLabelToolTip
            label={'Incident Details'}
            modalDetails={{
              data: entityDetails?.errorMessage,
            }}
            value={entityDetails?.errorMessage}
            {...(styles.textinputStyle as any)}
          />
        </Box>
        <SpacerNew size={80} direction="horizontal" />
        <ButtonNew type={'primarySmall'} width={240} title={'Retry'} onPress={resolveIncident} />
      </Box>
      <FullScreenLoader show={resolveIncidentHandler.state === 'pending'} />
    </Box>
  )
}

export default IncidentDetails

const styles = {
  container: {
    h: '100%',
    borderRadius: 8,
    shadowRadius: 8,
    shadowColor: '#0000001F' as ColorOptions,
  } as BoxProps,
  innerContainer: {
    flexDirection: 'row',
    flex: 1,
  } as BoxProps,
  textinputStyle: {
    borderColor: undefined,
    labelPaddingBottom: 8,
    borderRadius: 4,
    labelFontFamily: 'jiotype-medium',
    labelColor: '#757575' as ColorOptions,
    labelFontSize: 12,
    fontSize: 12,
    fontWeight: '500',
    selectionColor: 'BLUE',
    editable: false,
    paddingHorizontal: 0,
    color: 'BLACK_21',
    paddingVertical: 6,
    placeholderTextColor: '#757575' as ColorOptions,
    bgColor: 'TRANSPARENT',
    disabled: true,
    type: 'label',
  } as TextInputNewProps,
  keyText: (isObjectType) =>
    ({
      fontSize: isObjectType ? 16 : 14,
      color: isObjectType ? 'BLACK_21' : ('#757575' as any),
      fontFamily: isObjectType ? 'jiotype-Bold' : 'jiotype-medium',
    } as TextNewProps),
  keyDescText: {
    fontSize: 12,
    color: 'BLACK_21',
    fontFamily: 'jiotype-medium',
  } as TextNewProps,
}

const INCIDENT_DETAILS_STEPS = [
  'Select Parent',
  'Business Contact',
  'Authorised Signatory',
  'Business Details',
  'Other Business Details',
  'Bank Details',
  'Document Upload',
  'Fee/Package Details',
  'Kyc approval data',
  'Risk Settings',
]

const ONBOARDING_TERMINAL_UPDATE_TYPE = [
  'NEW_PROFILE',
  'UPDATE_PROFILE',
  'RISK_UPDATE_PROFILE',
  'RISK_COMMENT_ONLY',
  'BULK_TERMINAL_ADD',
]
